import { useEffect, useRef, useState } from "react";

/**
 * Lightweight scroll-triggered visibility hook (no external deps).
 * Returns [ref, isVisible]. Once visible, stays visible to avoid jank.
 *
 * Usage:
 *   const [ref, vis] = useInView();
 *   <div ref={ref} className={vis ? "animate-fade-up" : "opacity-0"}>...</div>
 */
export const useInView = (options = {}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setVisible(true);
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.15, ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return [ref, visible];
};

/**
 * Reveal wrapper: fades + translates up when scrolled into view.
 * Optional `delay` (ms) for staggered children.
 */
export const Reveal = ({ children, delay = 0, as: As = "div", className = "", ...rest }) => {
  const [ref, visible] = useInView();
  return (
    <As
      ref={ref}
      {...rest}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 700ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform 700ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
        willChange: "opacity, transform",
        ...(rest.style || {}),
      }}
      className={className}
    >
      {children}
    </As>
  );
};
